<template>
  <div>
    <data-table-wrapper
      :filter-config="filterConfig"
      :default-sort="{ by: 'period.start.timestamp', desc: true }"
      :default-items-per-page="15"
      :enable-search="true"
    >
      <template #default="{ tableProps, tableEvents, filters, updateFilter }">
        <v-data-table
          v-bind="tableProps"
          v-on="tableEvents"
          :headers="computedHeaders"
          :items="batches"
          item-key="meta.id"
          class="elevation-2"
          max-height="800px auto"
          no-data-text="Keine Abrechnungsberichte vorhanden"
          locale="de"
          @click:row="routeDetails"
        >
          <template v-slot:top>
            <v-card flat>
              <v-card-subtitle>
                <v-row>
                  <v-col>
                    <span class="mr-4">Filter nach Status</span>
                    <v-chip-group
                      :value="filters.status"
                      @change="updateFilter('status', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                        >Alle</v-chip
                      >
                      <v-chip
                        label
                        value="warningsToBeClosed"
                        class="transparent-background font-weight-medium mr-2"
                        >Warnungen prüfen</v-chip
                      >
                      <v-chip
                        label
                        value="payslipsToBeGenerated"
                        class="transparent-background font-weight-medium mr-2"
                        >Abrechnungszettel zu generieren</v-chip
                      >
                      <v-chip
                        label
                        value="paymentToBeInstructed"
                        class="transparent-background font-weight-medium mr-2"
                        >Auszahlung anzuweisen</v-chip
                      >
                      <v-chip
                        label
                        value="waitingOnPayment"
                        class="transparent-background font-weight-medium mr-2"
                        >Wartet auf Auszahlung</v-chip
                      >
                      <v-chip
                        label
                        value="paid"
                        class="transparent-background font-weight-medium mr-2"
                        >Ausgezahlt</v-chip
                      >
                    </v-chip-group>
                  </v-col>
                </v-row>
              </v-card-subtitle>
            </v-card>
            <v-divider></v-divider>
          </template>

          <template v-slot:group.header="{ items, isOpen, toggle }">
            <th :colspan="headers.length">
              <v-icon @click="toggle"
                >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
              {{ stripPrefix(items[0].groupBy) }}
            </th>
          </template>
          <template v-slot:[`item.user.displayName`]="{ item }">
            <div class="d-flex flex-column">
              <span
                class="d-block font-weight-semibold text--primary text-truncate"
                >{{ item.user.displayName }}</span
              >
              <small class="grey--text">Benutzergruppe</small>
            </div>
          </template>
          <template v-slot:[`item.period.start.timestamp`]="{ item }">
            {{ item.period.start.formattedTimestamp }}
          </template>
          <template v-slot:[`item.period.end.timestamp`]="{ item }">
            {{ item.period.end.formattedTimestamp }}
          </template>
          <template v-slot:[`item.type`]="{ item }">
            <v-chip outlined small>
              {{ getPayrollTypeText(item.type) }}
            </v-chip>
          </template>
          <template #[`item.status`]="{ item }">
            <payroll-status-chip :status="item.status" small>
            </payroll-status-chip>
          </template>
          <template #[`item.summary.totalCompensation.amount`]="{ item }">
            <pre
              >{{ item.summary?.totalCompensation?.amount }} {{
                item.summary?.totalCompensation?.currency
              }}</pre
            >
          </template>
          <template #[`item.actions`]="{ item }"
            ><v-btn
              v-if="showActionBtn"
              depressed
              @click="openItem(item, 'newTab')"
              small
              ><v-icon left small>mdi-open-in-app</v-icon>Öffnen</v-btn
            ><v-menu v-else offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon text v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list nav>
                <v-list-item link @click="routeDetails(item)">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-app</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Öffnen</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item link @click="openItem(item, 'newTab')">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Öffnen (in neuem Tab)</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item
                  link
                  :to="{
                    name: 'accounting-payroll-export',
                    params: {
                      organizationId: $route.params.organizationId,
                      itemId: item.meta.id,
                    },
                  }"
                  :disabled="
                    item.status === 'warningsToBeClosed' ||
                    item.status === 'payslipsToBeGenerated'
                  "
                >
                  <v-list-item-icon>
                    <v-icon
                      dense
                      :disabled="
                        item.status === 'warningsToBeClosed' ||
                        item.status === 'payslipsToBeGenerated'
                      "
                      >mdi-file-export</v-icon
                    >
                  </v-list-item-icon>
                  <v-list-item-title>Exportieren</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </template>
    </data-table-wrapper>
  </div>
</template>

<script>
import PayrollStatusChip from "@/components/accounting/payroll/PayrollStatusChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";

export default {
  name: "batches-data-table",
  props: {
    batches: {
      type: Array,
      required: true,
    },
  },
  components: {
    PayrollStatusChip,
    "data-table-wrapper": DataTableWrapper,
  },
  data() {
    return {
      filterConfig: {
        status: {
          default: "all",
          urlKey: "status",
        },
      },
    };
  },
  computed: {
    computedHeaders() {
      return [
        { text: "Bezeichnung", value: "title" },
        { text: "Typ", value: "type" },
        { text: "Abrechnung von", value: "period.start.timestamp" },
        { text: "Abrechnung bis", value: "period.end.timestamp" },
        { text: "Anzahl Abrechnungszettel", value: "persons.length" },
        { text: "Gesamtaufwand", value: "summary.totalCompensation.amount" },
        {
          text: "Status",
          value: "status",
          filter: (value) => {
            const statusFilter = this.$route.query.status || "all";
            if (statusFilter === "all") return true;
            return value === statusFilter;
          },
        },
        {
          sortKey: 100,
          value: "actions",
          default: true,
          sortable: false,
        },
      ];
    },
  },
  methods: {
    routeDetails(item) {
      this.$router.push({
        name: "accounting-payroll-details",
        params: {
          organizationId: this.$route.params.organizationId,
          itemId: item.meta.id,
        },
      });
    },
    getPayrollTypeText(type) {
      switch (type) {
        case "regular":
          return "Regulärer Turnus";
        case "special":
          return "Sonderzahlung";
        default:
          return "Unbekannt";
      }
    },
    openItem(item, type = "default") {
      if (type === "newTab") {
        window.open(
          this.$router.resolve({
            name: "accounting-payroll-details",
            params: {
              organizationId: this.$route.params.organizationId,
              itemId: item.meta.id,
            },
          }).href,
          "_blank"
        );
        return;
      } else {
        this.$router.push({
          name: "accounting-payroll-details",
          params: {
            organizationId: this.$route.params.organizationId,
            itemId: item.meta.id,
          },
        });
      }
    },
  },
};
</script>
